import { useEffect, useMemo, useState } from 'react';
import { CalendarPlus } from 'lucide-react';
import { useSandboxScheduleSession } from '@/services/mutations/sandbox';
import {
	SandboxField,
	SandboxModal,
	SandboxNotice,
	sandboxInputClass,
	sandboxSelectClass,
} from './SandboxModal';
import { fullName } from '../sandboxLabels';

interface Props {
	scenarioId: string;
	catalog: Sandbox.Catalog;
	users: Sandbox.ScenarioUser[];
	professionals: Sandbox.ScenarioProfessional[];
	initialUserId?: string | null;
	initialProfessionalId?: string | null;
	isOpen: boolean;
	onClose: () => void;
}

const PAYMENT_OPTIONS: Array<{
	key: Sandbox.SessionPaymentMode;
	label: string;
	hint: string;
}> = [
	{
		key: 'MANUAL',
		label: 'Pagamento manual confirmado',
		hint: 'Como o admin faz em Consultas: cria o pagamento manual e marca como pago.',
	},
	{
		key: 'BENEFIT',
		label: 'Usar benefício da assinatura',
		hint: 'Consome 1 uso do benefício do plano ativo do usuário (o que o checkout com 100% faria).',
	},
	{
		key: 'NONE',
		label: 'Deixar aguardando pagamento',
		hint: 'Sessão fica CREATED; expira sozinha em 30 min se ninguém pagar (regra real).',
	},
];

/** Agenda como o usuário faria (grade real do profissional) e opcionalmente paga. */
const SandboxSessionModal = ({
	scenarioId,
	catalog,
	users,
	professionals,
	initialUserId,
	initialProfessionalId,
	isOpen,
	onClose,
}: Props) => {
	const eligibleProfessionals = useMemo(
		() => professionals.filter((p) => p.authorized),
		[professionals],
	);
	const [userId, setUserId] = useState('');
	const [professionalId, setProfessionalId] = useState('');
	const [serviceId, setServiceId] = useState('');
	const [payment, setPayment] =
		useState<Sandbox.SessionPaymentMode>('MANUAL');
	const [selectedDate, setSelectedDate] = useState('');

	useEffect(() => {
		if (!isOpen) return;
		setUserId(
			initialUserId && users.some((u) => u.id === initialUserId)
				? initialUserId
				: users[0]?.id ?? '',
		);
		setProfessionalId(
			initialProfessionalId &&
				eligibleProfessionals.some(
					(p) => p.id === initialProfessionalId,
				)
				? initialProfessionalId
				: eligibleProfessionals[0]?.id ?? '',
		);
		setServiceId('');
		setPayment('MANUAL');
		setSelectedDate('');
	}, [
		isOpen,
		initialUserId,
		initialProfessionalId,
		users,
		eligibleProfessionals,
	]);

	const professional = eligibleProfessionals.find(
		(p) => p.id === professionalId,
	);
	const user = users.find((u) => u.id === userId);

	useEffect(() => {
		if (
			professional &&
			!professional.services.some((s) => s.id === serviceId)
		) {
			setServiceId(professional.services[0]?.id ?? '');
		}
	}, [professional, serviceId]);

	const { mutate, isPending } = useSandboxScheduleSession(
		scenarioId,
		onClose,
	);

	const submit = () => {
		mutate({
			user_id: userId,
			professional_id: professionalId,
			service_id: serviceId || undefined,
			selected_date: selectedDate
				? new Date(selectedDate).toISOString()
				: undefined,
			payment,
		});
	};

	const benefitWarning =
		payment === 'BENEFIT' && user && !user.active_subscription
			? 'Este usuário não tem assinatura ativa; o pagamento por benefício vai falhar.'
			: null;

	return (
		<SandboxModal
			isOpen={isOpen}
			onClose={onClose}
			title="Agendar sessão"
			description="Mesmo caminho do /agendamento: antecedência mínima, limite semanal, grade de horários e conflitos são validados de verdade."
			icon={<CalendarPlus className="h-4 w-4" />}
			confirmText="Agendar"
			onConfirm={submit}
			confirmDisabled={!userId || !professionalId}
			isPending={isPending}
		>
			<div className="space-y-4 py-2">
				{users.length === 0 || eligibleProfessionals.length === 0 ? (
					<SandboxNotice tone="warning">
						Crie ao menos um usuário e um profissional autorizado
						antes de agendar.
					</SandboxNotice>
				) : null}
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<SandboxField label="Usuário">
						<select
							className={sandboxSelectClass}
							value={userId}
							onChange={(e) => setUserId(e.target.value)}
						>
							{users.map((u) => (
								<option key={u.id} value={u.id}>
									{fullName(u)}
									{u.active_subscription
										? ` · ${u.active_subscription.club.name}`
										: ''}
								</option>
							))}
						</select>
					</SandboxField>
					<SandboxField label="Profissional">
						<select
							className={sandboxSelectClass}
							value={professionalId}
							onChange={(e) => setProfessionalId(e.target.value)}
						>
							{eligibleProfessionals.map((p) => (
								<option key={p.id} value={p.id}>
									{fullName(p)} · {p.availability_count}{' '}
									janela(s)
								</option>
							))}
						</select>
					</SandboxField>
					<SandboxField label="Serviço">
						<select
							className={sandboxSelectClass}
							value={serviceId}
							onChange={(e) => setServiceId(e.target.value)}
						>
							{(professional?.services ?? []).map((s) => (
								<option key={s.id} value={s.id}>
									{s.service_name}
								</option>
							))}
						</select>
					</SandboxField>
					<SandboxField
						label="Horário"
						hint="Vazio = primeiro horário livre da grade. Se informar, precisa ser um slot exato."
					>
						<input
							type="datetime-local"
							className={sandboxInputClass}
							value={selectedDate}
							onChange={(e) => setSelectedDate(e.target.value)}
						/>
					</SandboxField>
				</div>

				<SandboxField label="Pagamento">
					<div className="space-y-2">
						{PAYMENT_OPTIONS.map((option) => (
							<label
								key={option.key}
								className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${
									payment === option.key
										? 'border-stone-900 bg-stone-50'
										: 'border-stone-200 hover:border-stone-400'
								}`}
							>
								<input
									type="radio"
									name="sandbox-payment"
									className="mt-1 accent-stone-900"
									checked={payment === option.key}
									onChange={() => setPayment(option.key)}
								/>
								<span>
									<span className="block text-sm font-bold text-stone-900">
										{option.label}
									</span>
									<span className="block text-xs text-stone-400">
										{option.hint}
									</span>
								</span>
							</label>
						))}
					</div>
				</SandboxField>

				{benefitWarning ? (
					<SandboxNotice tone="warning">
						{benefitWarning}
					</SandboxNotice>
				) : null}
				<SandboxNotice>
					Regras do ambiente: até{' '}
					{catalog.rules.max_session_requests_per_user_per_week}{' '}
					sessão(ões) por usuário por semana; agenda de{' '}
					{catalog.rules.min_appointment_advance_days} a{' '}
					{catalog.rules.max_appointment_advance_days} dias à frente.
				</SandboxNotice>
			</div>
		</SandboxModal>
	);
};

export { SandboxSessionModal };
